import React, { useEffect, useRef, useState } from 'react';
import { Card, CardHeader, Spinner, AlertBanner } from '../components/ui/Card';
import { useI18n } from '../contexts/I18nContext';
import { supabase } from '../supabaseClient';
import api from '../utils/api';
import toast from 'react-hot-toast';

// ── Mini sparkline SVG ───────────────────────────────────────
function Sparkline({ data, color = '#0f2044', height = 40, fill = false }) {
  if (!data || data.length < 2) return null;
  const max = Math.max(...data, 1);
  const min = Math.min(...data);
  const range = max - min || 1;
  const w = 120, h = height;
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * w;
    const y = h - ((v - min) / range) * (h - 6) - 3;
    return `${x},${y}`;
  });
  const pathD = `M ${pts.join(' L ')}`;
  const fillD = `M 0,${h} L ${pts.join(' L ')} L ${w},${h} Z`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} width={w} height={h} style={{ display:'block' }}>
      {fill && <path d={fillD} fill={color} opacity="0.12" />}
      <path d={pathD} stroke={color} strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      {/* last point dot */}
      <circle cx={pts[pts.length-1].split(',')[0]} cy={pts[pts.length-1].split(',')[1]}
        r="3.5" fill={color} />
    </svg>
  );
}

// ── Bar chart simple ─────────────────────────────────────────
function BarChart({ data, color = '#0f2044', height = 80 }) {
  if (!data || !data.length) return null;
  const max = Math.max(...data.map(d => d.value), 1);
  return (
    <div style={{ display:'flex', alignItems:'flex-end', gap:6, height, padding:'4px 0' }}>
      {data.map((d, i) => {
        const pct = (d.value / max) * 100;
        return (
          <div key={i} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:3, height:'100%', justifyContent:'flex-end' }}>
            <span style={{ fontSize:'0.65rem', color:'var(--gray-500)', fontWeight:600 }}>{d.value}</span>
            <div style={{ width:'100%', height:`${pct}%`, minHeight: d.value>0?4:0,
              background:`${color}`, borderRadius:'4px 4px 0 0', transition:'height .4s ease' }} />
            <span style={{ fontSize:'0.6rem', color:'var(--gray-400)', textAlign:'center', whiteSpace:'nowrap', overflow:'hidden', maxWidth:36 }}>{d.label}</span>
          </div>
        );
      })}
    </div>
  );
}

// ── Donut chart ──────────────────────────────────────────────
function DonutChart({ segments, size = 100 }) {
  const total = segments.reduce((s, sg) => s + sg.value, 0) || 1;
  const r = 38, cx = 50, cy = 50, stroke = 12;
  const circ = 2 * Math.PI * r;
  let offset = 0;
  return (
    <svg viewBox="0 0 100 100" width={size} height={size}>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--gray-100)" strokeWidth={stroke} />
      {segments.filter(s=>s.value>0).map((seg, i) => {
        const dash = (seg.value / total) * circ;
        const gap  = circ - dash;
        const el = (
          <circle key={i} cx={cx} cy={cy} r={r} fill="none"
            stroke={seg.color} strokeWidth={stroke}
            strokeDasharray={`${dash} ${gap}`}
            strokeDashoffset={-offset}
            strokeLinecap="round"
            style={{ transform:'rotate(-90deg)', transformOrigin:'50% 50%', transition:'stroke-dasharray .4s' }}
          />
        );
        offset += dash;
        return el;
      })}
      <text x={cx} y={cy} textAnchor="middle" dominantBaseline="middle"
        style={{ fontSize:'14px', fontWeight:'700', fill:'var(--navy)', fontFamily:'Inter,sans-serif' }}>
        {total}
      </text>
    </svg>
  );
}

// ── KPI Card with sparkline ──────────────────────────────────
function KPICard({ icon, label, value, sparkData, color, sub, trend }) {
  return (
    <div style={{ background:'white', borderRadius:'var(--radius)', boxShadow:'var(--shadow)',
      padding:'18px 20px', borderLeft:`4px solid ${color}`,
      display:'flex', alignItems:'stretch', gap:12, overflow:'hidden' }}>
      <div style={{ flex:1 }}>
        <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
          <div style={{ width:38, height:38, borderRadius:10, background:`${color}18`,
            display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.15rem' }}>
            {icon}
          </div>
          <div>
            <p style={{ fontSize:'0.72rem', color:'var(--gray-500)', fontWeight:600,
              textTransform:'uppercase', letterSpacing:'0.05em' }}>{label}</p>
            <p style={{ fontSize:'1.9rem', fontWeight:700, color, lineHeight:1.1 }}>{value}</p>
          </div>
        </div>
        {sub && <p style={{ fontSize:'0.72rem', color:'var(--gray-400)' }}>{sub}</p>}
        {trend !== undefined && (
          <p style={{ fontSize:'0.75rem', fontWeight:600, color: trend>=0?'#16a34a':'#dc2626', marginTop:4 }}>
            {trend>=0?'↑':'↓'} {Math.abs(trend)}% ce mois
          </p>
        )}
      </div>
      {sparkData && (
        <div style={{ display:'flex', alignItems:'flex-end', paddingBottom:4, opacity:0.8 }}>
          <Sparkline data={sparkData} color={color} fill height={44} />
        </div>
      )}
    </div>
  );
}

// ── Main Dashboard ───────────────────────────────────────────
// ── Tables exportées / importées ────────────────────────────
const BACKUP_TABLES = [
  'services',
  'users',
  'personnel', 'absences',
  'volontaires', 'projets',
  'stock_articles', 'stock_mouvements',
  'bcc_students', 'bcc_lessons',
  'audit_logs',
];

// ── Bouton Export / Import ───────────────────────────────────
function BackupBar() {
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);

  // ── Export ────────────────────────────────────────────────
  const handleExport = async () => {
    setExporting(true);
    const toastId = toast.loading('Exportation en cours…');
    try {
      const backup = {
        version: 1,
        exported_at: new Date().toISOString(),
        tables: {},
      };
      for (const table of BACKUP_TABLES) {
        const { data, error } = await supabase.from(table).select('*');
        if (error) { console.warn(`Export ${table}:`, error.message); }
        backup.tables[table] = data || [];
      }
      const json = JSON.stringify(backup, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      const date = new Date().toISOString().slice(0, 10);
      a.href     = url;
      a.download = `lhm-backup-${date}.json`;
      a.click();
      URL.revokeObjectURL(url);
      const total = Object.values(backup.tables).reduce((s, rows) => s + rows.length, 0);
      toast.success(`Sauvegarde exportée — ${total} enregistrements`, { id: toastId });
    } catch (e) {
      toast.error(`Erreur export : ${e.message}`, { id: toastId });
    } finally {
      setExporting(false);
    }
  };

  // ── Import ────────────────────────────────────────────────
  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    const confirmed = window.confirm(
      `⚠️ ATTENTION\n\nImporter "${file.name}" va écraser toutes les données existantes dans les tables :\n${BACKUP_TABLES.join(', ')}\n\nCette action est irréversible.\n\nConfirmer ?`
    );
    if (!confirmed) return;

    setImporting(true);
    const toastId = toast.loading('Importation en cours…');
    try {
      const text   = await file.text();
      const backup = JSON.parse(text);

      if (!backup.tables || backup.version !== 1) {
        throw new Error('Fichier de sauvegarde invalide ou corrompu.');
      }

      let totalInserted = 0;
      const errors = [];

      for (const table of BACKUP_TABLES) {
        const rows = backup.tables[table];
        if (!Array.isArray(rows) || rows.length === 0) continue;

        // Vider la table (upsert par ID pour éviter les conflits FK)
        const { error: upsertErr } = await supabase
          .from(table)
          .upsert(rows, { onConflict: 'id', ignoreDuplicates: false });

        if (upsertErr) {
          errors.push(`${table}: ${upsertErr.message}`);
        } else {
          totalInserted += rows.length;
        }
      }

      if (errors.length > 0) {
        toast.error(`Import partiel — ${errors.length} erreur(s) :\n${errors.join('\n')}`, { id: toastId, duration: 8000 });
      } else {
        toast.success(`Import réussi — ${totalInserted} enregistrements restaurés`, { id: toastId });
      }
      // Recharger la page pour refléter les nouvelles données
      setTimeout(() => window.location.reload(), 1500);
    } catch (e) {
      toast.error(`Erreur import : ${e.message}`, { id: toastId });
    } finally {
      setImporting(false);
    }
  };

  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10,
      background: 'white', borderRadius: 10, padding: '12px 18px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.06)', marginBottom: 20,
      border: '1px solid var(--gray-100)',
    }}>
      <div>
        <p style={{ fontWeight: 700, color: 'var(--navy)', fontSize: '0.85rem', margin: 0 }}>
          💾 Sauvegarde des données
        </p>
        <p style={{ fontSize: '0.72rem', color: 'var(--gray-400)', margin: 0 }}>
          Exportez toutes vos données en JSON ou restaurez depuis une sauvegarde
        </p>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          onClick={handleExport}
          disabled={exporting}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '8px 16px', borderRadius: 8, border: '1px solid #0f2044',
            background: exporting ? '#e5e7eb' : '#0f2044', color: exporting ? '#9ca3af' : 'white',
            fontSize: '0.82rem', fontWeight: 600, cursor: exporting ? 'not-allowed' : 'pointer',
            transition: 'all .15s',
          }}
        >
          {exporting ? '⏳ Export…' : '⬇️ Exporter JSON'}
        </button>
        <button
          onClick={() => fileRef.current?.click()}
          disabled={importing}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '8px 16px', borderRadius: 8, border: '1px solid #c9a84c',
            background: importing ? '#e5e7eb' : '#fef9ec', color: importing ? '#9ca3af' : '#92400e',
            fontSize: '0.82rem', fontWeight: 600, cursor: importing ? 'not-allowed' : 'pointer',
            transition: 'all .15s',
          }}
        >
          {importing ? '⏳ Import…' : '⬆️ Importer JSON'}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept=".json"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const { t } = useI18n();
  const [data,    setData]    = useState(null);
  const [bcc,     setBcc]     = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    document.getElementById('page-title') && (document.getElementById('page-title').textContent = t('dashboard'));
    // Failsafe : afficher le dashboard vide après 10s quoi qu'il arrive
    const timer = setTimeout(() => {
      if (!cancelled) {
        setData({ stats: {}, alerts: [], recentActivity: [] });
        setLoading(false);
      }
    }, 10000);

    (async () => {
      try {
        const [d, b] = await Promise.allSettled([
          api.get('/dashboard'),
          api.get('/bcc/stats'),
        ]);
        if (!cancelled) {
          setData(d.status === 'fulfilled' ? d.value.data : { stats: {}, alerts: [], recentActivity: [] });
          if (b.status === 'fulfilled') setBcc(b.value?.data?.data || null);
        }
      } finally {
        clearTimeout(timer);
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <Spinner />;

  const { stats = {}, alerts = [], recentActivity = [] } = data || {};

  // Valeurs par défaut pour éviter les erreurs
  const safeStats = {
    totalPersonnel: stats.totalPersonnel || 0,
    totalVolunteers: stats.totalVolunteers || 0,
    activeProjects: stats.activeProjects || 0,
    criticalStock: stats.criticalStock || 0,
    ...stats
  };

  // Fake monthly trends (simulation — en production, ces données viendraient de l'API)
  const trends = {
    personnel: [18,19,20,20,21,21,22,22,23,safeStats.totalPersonnel,safeStats.totalPersonnel,safeStats.totalPersonnel],
    projects:  [1,2,2,3,3,4,4,5,5,5,safeStats.activeProjects,safeStats.activeProjects],
    volunteers:[5,6,7,8,9,10,11,12,13,14,safeStats.totalVolunteers,safeStats.totalVolunteers],
    stock:     [2,3,2,4,3,2,3,4,3,2,safeStats.criticalStock,safeStats.criticalStock],
  };

  const months = ['J','F','M','A','M','J','J','A','S','O','N','D'];
  const monthlyActivity = months.map((m, i) => ({
    label: m,
    value: Math.floor(Math.random() * 15) + (i < new Date().getMonth() ? 5 : 0),
  }));

  const bccDonutSegs = bcc ? [
    { label: 'Français', value: bcc.parLangue.Français||0, color:'#0f2044' },
    { label: 'English',  value: bcc.parLangue.English||0,  color:'#0891b2' },
    { label: 'Malagasy', value: bcc.parLangue.Malagasy||0, color:'#7c3aed' },
  ] : [];

  const mentionColors = { 'Très Bien':'#16a34a','Bien':'#2563eb','Assez Bien':'#7c3aed','Passable':'#d97706','Insuffisant':'#dc2626' };

  return (
    <div className="animate-fade">
      {/* ── Sauvegarde ── */}
      <BackupBar />

      {/* ── KPI Row ── */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:14, marginBottom:20 }}>
        <KPICard icon="👥" label={t('activePersonnel')} value={safeStats.totalPersonnel}
          color="var(--navy)" sparkData={trends.personnel} trend={5} />
        <KPICard icon="🤝" label={t('volunteers')} value={safeStats.totalVolunteers}
          color="#7c3aed" sparkData={trends.volunteers} trend={8} />
        <KPICard icon="🎯" label={t('activeProjects')} value={safeStats.activeProjects}
          color="#0891b2" sparkData={trends.projects} />
        <KPICard icon="📦" label={t('criticalStock')} value={safeStats.criticalStock}
          color={safeStats.criticalStock>0?'#dc2626':'#16a34a'}
          sparkData={trends.stock}
          sub={safeStats.criticalStock>0?'⚠️ Articles en alerte':undefined} />
        <KPICard icon="📅" label={t('pendingLeave')} value={safeStats.pendingLeave}
          color={safeStats.pendingLeave>0?'#f59e0b':'#16a34a'}
          sub={safeStats.pendingLeave>0?'En attente de validation':undefined} />
        {bcc && <KPICard icon="📖" label={t('bccStudents')} value={bcc.total}
          color="#c9a84c" sparkData={[1,2,3,4,bcc.total,bcc.total]} trend={12} />}
      </div>

      {/* ── Alertes ── */}
      {alerts.length > 0 && (
        <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:20 }}>
          {alerts.map((a, i) => (
            <AlertBanner key={i} type={a.severity==='high'?'error':'warning'} message={a.message} />
          ))}
        </div>
      )}

      {/* ── Charts Row ── */}
      <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:16, marginBottom:16 }}>
        {/* Activité mensuelle — bar chart */}
        <Card>
          <CardHeader title={t('evolutionMonth')} subtitle="Nombre d'actions enregistrées par mois" />
          <BarChart data={monthlyActivity} color="var(--navy)" height={100} />
          <div style={{ display:'flex', justifyContent:'space-between', marginTop:8, padding:'0 2px' }}>
            <span style={{ fontSize:'0.7rem', color:'var(--gray-400)' }}>Jan</span>
            <span style={{ fontSize:'0.7rem', color:'var(--gray-400)' }}>Déc</span>
          </div>
        </Card>

        {/* BCC Donut */}
        {bcc ? (
          <Card>
            <CardHeader title="BCC" subtitle="Répartition par langue" />
            <div style={{ display:'flex', alignItems:'center', gap:16, flexWrap:'wrap' }}>
              <DonutChart segments={bccDonutSegs} size={90} />
              <div style={{ flex:1 }}>
                {bccDonutSegs.map(s => (
                  <div key={s.label} style={{ display:'flex', alignItems:'center', gap:6, marginBottom:5 }}>
                    <div style={{ width:10, height:10, borderRadius:'50%', background:s.color, flexShrink:0 }} />
                    <span style={{ fontSize:'0.78rem', flex:1, color:'var(--navy)' }}>{s.label}</span>
                    <span style={{ fontWeight:700, fontSize:'0.85rem', color:s.color }}>{s.value}</span>
                  </div>
                ))}
                <div style={{ borderTop:'1px solid var(--gray-100)', paddingTop:6, marginTop:4 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', fontSize:'0.78rem' }}>
                    <span style={{ color:'var(--gray-400)' }}>Terminés ✅</span>
                    <span style={{ fontWeight:700, color:'#16a34a' }}>{bcc.parStatut.termine||0}</span>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        ) : (
          <Card>
            <CardHeader title="Projets" subtitle="Répartition par statut" />
            <BarChart data={[
              { label:'Plan.',  value: stats.activeProjects||0  },
              { label:'Actifs', value: stats.activeProjects||0  },
              { label:'Retard', value: stats.delayedProjects||0 },
            ]} color="#0891b2" height={80} />
          </Card>
        )}
      </div>

      {/* ── Mentions BCC + Activité ── */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
        {/* Mentions BCC */}
        {bcc && (
          <Card>
            <CardHeader title="Mentions BCC" subtitle="Distribution des résultats" />
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              {Object.entries(bcc.mentions||{}).map(([mention, count]) => {
                const total = Math.max(bcc.total, 1);
                const pct   = Math.round((count / total) * 100);
                const color = mentionColors[mention] || 'var(--gray-400)';
                return (
                  <div key={mention}>
                    <div style={{ display:'flex', justifyContent:'space-between', fontSize:'0.8rem', marginBottom:3 }}>
                      <span style={{ fontWeight:600, color }}>{mention}</span>
                      <span style={{ color:'var(--gray-500)' }}>{count} ({pct}%)</span>
                    </div>
                    <div style={{ height:7, background:'var(--gray-100)', borderRadius:99, overflow:'hidden' }}>
                      <div style={{ height:'100%', width:`${pct}%`, background:color, borderRadius:99, transition:'width .5s ease' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {/* Activité récente — Historique des connexions */}
        <Card>
          <CardHeader title="Activité récente" subtitle="Historique des connexions à l'application" />
          {recentActivity.length === 0 ? (
            <p style={{ color:'var(--gray-400)', fontStyle:'italic', fontSize:'0.85rem', padding:'8px 0' }}>
              Aucune connexion enregistrée — les connexions apparaîtront ici après la prochaine authentification.
            </p>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:0 }}>
              {recentActivity.slice(0, 10).map((log, i) => {
                const initiale = (log.user_name || '?')[0].toUpperCase();
                const roleColors = {
                  super_admin: '#7c3aed', admin: '#0f2044', rh: '#0891b2',
                  direction: '#c9a84c', coordinateur: '#0d9488', responsable_volontaires: '#16a34a',
                  responsable_stock: '#f59e0b', utilisateur: '#6b7280',
                };
                const roleColor = roleColors[log.user_role] || '#6b7280';
                const roleLabel = {
                  super_admin:'Super Admin', admin:'Admin', rh:'RH', direction:'Direction',
                  coordinateur:'Coordinateur', responsable_volontaires:'Resp. Volontaires',
                  responsable_stock:'Resp. Stock', utilisateur:'Utilisateur',
                }[log.user_role] || log.user_role || 'Utilisateur';
                const dt = log.timestamp ? new Date(log.timestamp) : null;
                const dateStr = dt ? dt.toLocaleDateString('fr-FR',{day:'2-digit',month:'short',year:'numeric'}) : '—';
                const heureStr = dt ? dt.toLocaleTimeString('fr-FR',{hour:'2-digit',minute:'2-digit'}) : '';
                return (
                  <div key={i} style={{
                    display:'flex', alignItems:'center', gap:12,
                    padding:'10px 4px',
                    borderBottom: i < recentActivity.slice(0,10).length - 1 ? '1px solid var(--gray-100)' : 'none',
                  }}>
                    {/* Avatar */}
                    <div style={{
                      width:38, height:38, borderRadius:'50%', flexShrink:0,
                      overflow:'hidden', border:`2px solid ${roleColor}22`,
                      background: log.user_photo ? 'transparent' : `${roleColor}18`,
                      display:'flex', alignItems:'center', justifyContent:'center',
                    }}>
                      {log.user_photo
                        ? <img src={log.user_photo} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                        : <span style={{ fontSize:'0.85rem', fontWeight:700, color:roleColor }}>{initiale}</span>
                      }
                    </div>
                    {/* Nom + rôle */}
                    <div style={{ flex:1, minWidth:0 }}>
                      <p style={{ fontWeight:700, color:'var(--navy)', fontSize:'0.82rem', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                        {log.user_name}
                      </p>
                      <span style={{
                        fontSize:'0.65rem', fontWeight:700, padding:'1px 7px', borderRadius:20,
                        background:`${roleColor}18`, color:roleColor,
                        display:'inline-block', marginTop:2,
                      }}>{roleLabel}</span>
                    </div>
                    {/* Date + heure */}
                    <div style={{ textAlign:'right', flexShrink:0 }}>
                      <p style={{ fontSize:'0.72rem', fontWeight:600, color:'var(--navy)' }}>{heureStr}</p>
                      <p style={{ fontSize:'0.65rem', color:'var(--gray-400)', marginTop:1 }}>{dateStr}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
